import { useState, useEffect } from "react";
import { X } from "lucide-react";
import LibrariesList from "../../videoLibaries/VideoLibaries";
const apiBase = process.env.REACT_APP_API;

// Modal component for updating lesson data (PDF or Video)
export default function LessonUpdateModal({
  lessonId,
  lessonType,
  isOpen = true,
  onClose,
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(null);
  const [libraryId, setLibraryId] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // Fetch the lesson data when the modal opens
  useEffect(() => {
    if (isOpen && lessonId) {
      fetchLessonData();
    }
  }, [isOpen, lessonId, lessonType]);

  // Load initial data based on lesson type
  const fetchLessonData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const url =
        lessonType === "pdf"
          ? `${apiBase}/lessons/pdf/${lessonId}`
          : `${apiBase}/lessons/video/${lessonId}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to load ${lessonType} data. Status: ${response.status}`
        );
      }

      const data = await response.json();

      // Initialize form data based on lesson type
      if (lessonType === "pdf") {
        setFormData({
          url: data.URL || "",
          pdfTitle: data.title || "",
          status: data.status || 1,
        });
      } else {
        setFormData({
          videoID: data.URL || "",
          videoTitle: data.title || "",
          isDriveVideo: data.isDriveVideo || false,
          videoLibraryId: data.bunney_library_id,
          status: data.status || 0,
          duration: data.allowed_time,
        });
        setLibraryId(data.bunney_library_id);
      }
    } catch (err) {
      setError(
        err.message || "An error occurred while loading the lesson data"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccessMessage("");
    try {
      const url =
        lessonType === "pdf"
          ? `${apiBase}/lessons/pdf/${lessonId}`
          : `${apiBase}/lessons/video/${lessonId}`;

      // Create the data to send, including libraryId for video lessons
      const dataToSend =
        lessonType === "video"
          ? {
              ...formData,
              allowedTime: formData.duration,
              videoLibraryId: libraryId,
            }
          : formData;

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to update ${lessonType}. Status: ${response.status}`
        );
      }

      setSuccessMessage(
        `${lessonType.toUpperCase()} lesson updated successfully!`
      );

      // Close the modal after successful update (after a short delay)
      setTimeout(() => {
        onClose();
        setSuccessMessage(null);
      }, 1500);
    } catch (err) {
      setError(err.message || "An error occurred while updating the lesson");
    } finally {
      setIsSaving(false);
    }
  };

  // If the modal is not open, don't render anything
  if (!isOpen) return null;

  console.log("fetched libary id ", libraryId);
  console.log(formData);
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg w-full max-w-md p-6 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">
            Update {lessonType === "pdf" ? "PDF" : "Video"} Lesson
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Different form fields based on lesson type */}
            {lessonType === "pdf" ? (
              <>
                <div className="mb-4">
                  <label
                    className="block text-gray-700 text-sm font-bold mb-2"
                    htmlFor="url"
                  >
                    PDF URL
                  </label>
                  <input
                    type="text"
                    id="url"
                    name="url"
                    value={formData.url}
                    onChange={handleChange}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-gray-700 text-sm font-bold mb-2"
                    htmlFor="pdfTitle"
                  >
                    PDF Title
                  </label>
                  <input
                    type="text"
                    id="pdfTitle"
                    name="pdfTitle"
                    value={formData.pdfTitle}
                    onChange={handleChange}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-gray-700 text-sm font-bold mb-2"
                    htmlFor="status"
                  >
                    Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </>
            ) : (
              <>
                <div className="mb-4">
                  <label
                    className="block text-gray-700 text-sm font-bold mb-2"
                    htmlFor="videoID"
                  >
                    Video ID
                  </label>
                  <input
                    type="text"
                    id="videoID"
                    name="videoID"
                    value={formData.videoID}
                    onChange={handleChange}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-gray-700 text-sm font-bold mb-2"
                    htmlFor="videoTitle"
                  >
                    Video Title
                  </label>
                  <input
                    type="text"
                    id="videoTitle"
                    name="videoTitle"
                    value={formData.videoTitle}
                    onChange={handleChange}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  />
                </div>
                {!formData.isDriveVideo && (
                  <div className="mb-4">
                    <label
                      className="block text-gray-700 text-sm font-bold mb-2"
                      htmlFor="videoTitle"
                    >
                      Duration
                    </label>
                    <input
                      type="number"
                      id="duration"
                      name="duration"
                      value={formData.duration}
                      onChange={handleChange}
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      required
                    />
                  </div>
                )}

                <div className="mb-4 flex items-center">
                  <input
                    type="checkbox"
                    id="isDriveVideo"
                    name="isDriveVideo"
                    checked={formData.isDriveVideo}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <label
                    className="text-gray-700 text-sm font-bold"
                    htmlFor="isDriveVideo"
                  >
                    Is Google Drive Video
                  </label>
                </div>
                {!formData.isDriveVideo && (
                  <div>
                    <LibrariesList
                      libraryId={libraryId}
                      setLibraryId={setLibraryId}
                    />
                  </div>
                )}
                <div className="mb-4">
                  <label
                    className="block text-gray-700 text-sm font-bold mb-2"
                    htmlFor="status"
                  >
                    Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  >
                    <option value={0}>Inactive</option>
                    <option value={1}>Active</option>
                  </select>
                </div>
              </>
            )}

            {successMessage && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                {successMessage}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
